// 📄 pages/Proyectos/components/RepositorioComunicacion.tsx
// Sub-fase K5 (refactor de Configuraciones Cloud/On-Premise): único punto de
// entrada al pool de comunicación de un proyecto S/4HANA Cloud — Acceso al
// Tenant + Scopes Habilitados (cloudConfigService.ts) y el pool de
// Communication Users/Systems (comunicacionService.ts, K4, ya tenía
// servicio+rules pero ninguna UI todavía). Se accede desde una tarjeta
// Cloud-only en IntegrationSection.tsx (movida ahí en K9 a pedido del
// usuario — es config de canales de las soluciones, no una herramienta de
// catálogo) y desde el redirect dentro de "Acceso al Tenant" (K8). La
// ruta en sí no está gateada por rol, solo por dónde se linkea desde.
import { useEffect, useState } from 'react';
import { Building2, Users, Server, Plus, RefreshCw, Pencil, Trash2, KeyRound, Tag, X } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';
import { useCredentials } from '../../../hooks/useCredentials.ts';
import { getCloudConfig, saveCloudConfig, CloudConfigData, emptyCloudConfig } from '../../../services/cloudConfigService.ts';
import {
  getComunicacionUsers, crearComunicacionUser, actualizarComunicacionUser, eliminarComunicacionUser,
  getComunicacionSystems, crearComunicacionSystem, actualizarComunicacionSystem, eliminarComunicacionSystem,
  ComunicacionUser, ComunicacionSystem
} from '../../../services/comunicacionService.ts';
import ComunicacionUserModal from './ComunicacionUserModal.tsx';
import ComunicacionSystemModal from './ComunicacionSystemModal.tsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';
import Alert from '../../../components/ui/Alert.tsx';

interface Props {
  projectId: string;
}

type Tab = 'tenant' | 'users' | 'systems';

export default function RepositorioComunicacion({ projectId }: Props) {
  const { user, profile } = useAuth();
  const esAdmin = profile?.role === 'admin';
  const { credentials } = useCredentials(projectId);

  const [tab, setTab] = useState<Tab>('tenant');
  const [loading, setLoading] = useState(true);

  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState<'success' | 'error' | 'warning'>('success');
  const showAlert = (msg: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(''), 4000);
  };

  // ── Acceso al Tenant + Scopes ─────────────────────────────────────────
  const [cloudConfig, setCloudConfig] = useState<CloudConfigData>(emptyCloudConfig());
  const [whitelistText, setWhitelistText] = useState('');
  const [nuevoScope, setNuevoScope] = useState('');
  const [guardandoTenant, setGuardandoTenant] = useState(false);

  // ── Communication Users ───────────────────────────────────────────────
  const [usuarios, setUsuarios] = useState<ComunicacionUser[]>([]);
  const [modalUsuarioOpen, setModalUsuarioOpen] = useState(false);
  const [editandoUsuario, setEditandoUsuario] = useState<ComunicacionUser | null>(null);
  const [aEliminarUsuario, setAEliminarUsuario] = useState<ComunicacionUser | null>(null);

  // ── Communication Systems ─────────────────────────────────────────────
  const [sistemas, setSistemas] = useState<ComunicacionSystem[]>([]);
  const [modalSistemaOpen, setModalSistemaOpen] = useState(false);
  const [editandoSistema, setEditandoSistema] = useState<ComunicacionSystem | null>(null);
  const [aEliminarSistema, setAEliminarSistema] = useState<ComunicacionSystem | null>(null);

  const cargarTodo = async () => {
    setLoading(true);
    try {
      const [cfg, us, ss] = await Promise.all([
        getCloudConfig(projectId), getComunicacionUsers(projectId), getComunicacionSystems(projectId)
      ]);
      setCloudConfig(cfg);
      setWhitelistText(cfg.whitelistIps.join('\n'));
      setUsuarios(us);
      setSistemas(ss);
    } catch (err) {
      console.error('Error cargando el Repositorio de Comunicación:', err);
      showAlert('No se pudo cargar el Repositorio de Comunicación.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    cargarTodo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleGuardarTenant = async () => {
    setGuardandoTenant(true);
    try {
      const data: CloudConfigData = {
        ...cloudConfig,
        whitelistIps: whitelistText.split('\n').map((s) => s.trim()).filter(Boolean)
      };
      await saveCloudConfig(projectId, data);
      setCloudConfig(data);
      showAlert('Acceso al Tenant guardado correctamente.');
    } catch (err: any) {
      showAlert(err.message || 'Error al guardar el Acceso al Tenant.', 'error');
    } finally {
      setGuardandoTenant(false);
    }
  };

  const agregarScope = () => {
    const valor = nuevoScope.trim().toUpperCase();
    if (!valor || cloudConfig.scopesHabilitados.includes(valor)) { setNuevoScope(''); return; }
    setCloudConfig({ ...cloudConfig, scopesHabilitados: [...cloudConfig.scopesHabilitados, valor] });
    setNuevoScope('');
  };

  const quitarScope = (scope: string) => {
    setCloudConfig({ ...cloudConfig, scopesHabilitados: cloudConfig.scopesHabilitados.filter((s) => s !== scope) });
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <p className="text-sm">Cargando Repositorio de Comunicación...</p>
      </div>
    );
  }

  const TABS: { id: Tab; label: string; icon: typeof Building2 }[] = [
    { id: 'tenant', label: 'Acceso al Tenant', icon: Building2 },
    { id: 'users', label: 'Communication Users', icon: Users },
    { id: 'systems', label: 'Communication Systems', icon: Server }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Alert message={alertMessage} type={alertType} onClose={() => setAlertMessage('')} />

      <div className="flex flex-wrap gap-1.5 bg-white/5 border border-white/10 rounded-2xl p-1.5">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex-1 min-w-[160px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${
                tab === t.id ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30' : 'text-gray-400 hover:text-white border border-transparent'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Acceso al Tenant ── */}
      {tab === 'tenant' && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">URL base del tenant</label>
            <input
              value={cloudConfig.tenantUrl}
              onChange={(e) => setCloudConfig({ ...cloudConfig, tenantUrl: e.target.value })}
              placeholder="https://miempresa.s4hana.cloud.sap"
              className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Whitelist IPs (una por línea)</label>
            <textarea
              value={whitelistText}
              onChange={(e) => setWhitelistText(e.target.value)}
              rows={3}
              placeholder={'203.0.113.10\n203.0.113.11'}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <KeyRound className="w-3.5 h-3.5" /> Credenciales admin
            </label>
            <select
              value={cloudConfig.credencialAdminId}
              onChange={(e) => setCloudConfig({ ...cloudConfig, credencialAdminId: e.target.value })}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Sin asignar</option>
              {credentials.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre} ({c.ambiente})</option>
              ))}
            </select>
            <p className="text-[11px] text-gray-500 mt-1">
              Referencia al vault de Credenciales del proyecto — la contraseña nunca se guarda acá.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5" /> Scopes habilitados
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {cloudConfig.scopesHabilitados.map((s) => (
                <span key={s} className="flex items-center gap-1 text-[11px] font-mono bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 px-2 py-1 rounded-full">
                  {s}
                  <button type="button" onClick={() => quitarScope(s)} className="hover:text-white"><X className="w-3 h-3" /></button>
                </span>
              ))}
              {cloudConfig.scopesHabilitados.length === 0 && <span className="text-xs text-gray-500">Ninguno todavía.</span>}
            </div>
            <div className="flex gap-2">
              <input
                value={nuevoScope}
                onChange={(e) => setNuevoScope(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarScope(); } }}
                placeholder="ej. 4Z2"
                className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
              />
              <button type="button" onClick={agregarScope} className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-lg text-xs font-semibold transition-all">
                <Plus className="w-3.5 h-3.5" /> Agregar
              </button>
            </div>
          </div>

          <div className="flex justify-end pt-2 border-t border-white/10">
            <button
              type="button"
              onClick={handleGuardarTenant}
              disabled={guardandoTenant}
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
            >
              {guardandoTenant ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Guardar Acceso al Tenant'}
            </button>
          </div>
        </div>
      )}

      {/* ── Communication Users ── */}
      {tab === 'users' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-300">Communication Users</h3>
            <button
              type="button"
              onClick={() => setModalUsuarioOpen(true)}
              className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Nuevo Communication User
            </button>
          </div>

          {usuarios.length === 0 ? (
            <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
              <Users className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm">Este proyecto todavía no tiene Communication Users en su pool.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {usuarios.map((u) => (
                <div key={u.id} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-mono font-semibold text-white">{u.entornos.DEV.nombre || '(sin username en DEV)'}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                        u.tipo === 'inbound' ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30' : 'text-amber-300 bg-amber-500/10 border-amber-500/30'
                      }`}>
                        {u.tipo}
                      </span>
                    </div>
                    {u.descripcion && <p className="text-xs text-gray-400 mt-1">{u.descripcion}</p>}
                  </div>
                  <button onClick={() => setEditandoUsuario(u)} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0" title="Editar">
                    <Pencil className="w-4 h-4" />
                  </button>
                  {esAdmin && (
                    <button onClick={() => setAEliminarUsuario(u)} className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0" title="Eliminar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Communication Systems ── */}
      {tab === 'systems' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-300">Communication Systems</h3>
            <button
              type="button"
              onClick={() => setModalSistemaOpen(true)}
              className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Nuevo Communication System
            </button>
          </div>

          {sistemas.length === 0 ? (
            <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
              <Server className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm">Este proyecto todavía no tiene Communication Systems en su pool.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {sistemas.map((s) => {
                const userDeSistema = usuarios.find((u) => u.id === s.comunicacionUserId);
                return (
                  <div key={s.id} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-mono font-semibold text-white">{s.nombre}</span>
                        <span className="text-[10px] text-gray-400">
                          → {userDeSistema?.entornos.DEV.nombre || 'Communication User no encontrado'}
                        </span>
                      </div>
                      {s.descripcion && <p className="text-xs text-gray-400 mt-1">{s.descripcion}</p>}
                    </div>
                    <button onClick={() => setEditandoSistema(s)} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0" title="Editar">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {esAdmin && (
                      <button onClick={() => setAEliminarSistema(s)} className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0" title="Eliminar">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {(modalUsuarioOpen || editandoUsuario) && (
        <ComunicacionUserModal
          usuario={editandoUsuario}
          onSave={async (data) => {
            if (editandoUsuario) await actualizarComunicacionUser(projectId, editandoUsuario.id, data);
            else await crearComunicacionUser(projectId, data, user?.uid || '');
            await cargarTodo();
            showAlert(editandoUsuario ? 'Communication User actualizado.' : 'Communication User creado.');
          }}
          onClose={() => { setModalUsuarioOpen(false); setEditandoUsuario(null); }}
        />
      )}

      {(modalSistemaOpen || editandoSistema) && (
        <ComunicacionSystemModal
          sistema={editandoSistema}
          usuarios={usuarios}
          onSave={async (data) => {
            if (editandoSistema) await actualizarComunicacionSystem(projectId, editandoSistema.id, data);
            else await crearComunicacionSystem(projectId, data, user?.uid || '');
            await cargarTodo();
            showAlert(editandoSistema ? 'Communication System actualizado.' : 'Communication System creado.');
          }}
          onClose={() => { setModalSistemaOpen(false); setEditandoSistema(null); }}
        />
      )}

      {aEliminarUsuario && (
        <ConfirmDialog
          title="¿Eliminar este Communication User?"
          message={`"${aEliminarUsuario.entornos.DEV.nombre || aEliminarUsuario.id}" se eliminará del pool. Si algún Communication System lo referencia, quedará roto — revisá antes de confirmar.`}
          confirmLabel="Eliminar"
          danger
          onConfirm={async () => {
            try {
              await eliminarComunicacionUser(projectId, aEliminarUsuario.id);
              setAEliminarUsuario(null);
              await cargarTodo();
              showAlert('Communication User eliminado.');
            } catch (err: any) {
              showAlert(err.message || 'Error al eliminar.', 'error');
            }
          }}
          onCancel={() => setAEliminarUsuario(null)}
        />
      )}

      {aEliminarSistema && (
        <ConfirmDialog
          title="¿Eliminar este Communication System?"
          message={`"${aEliminarSistema.nombre}" se eliminará del pool. Esta acción no se puede deshacer.`}
          confirmLabel="Eliminar"
          danger
          onConfirm={async () => {
            try {
              await eliminarComunicacionSystem(projectId, aEliminarSistema.id);
              setAEliminarSistema(null);
              await cargarTodo();
              showAlert('Communication System eliminado.');
            } catch (err: any) {
              showAlert(err.message || 'Error al eliminar.', 'error');
            }
          }}
          onCancel={() => setAEliminarSistema(null)}
        />
      )}
    </div>
  );
}
